import React, { useEffect, useRef, useState } from "react";
import { useUser } from "@/utils/userContext/UserContext";
import { useGlobalModal } from "@/components/GlobalModalProvider/GlobalModalProvider";
import api from "@/utils/connector/ApiConnector";
import { BookableProduct, BookingApi } from "../hooks/useBookings";

interface BookingModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    productId: string;
    customerId: string;
    startTime: string;
    endTime: string;
    isFullDay: boolean;
    notes?: string;
  }) => Promise<void>;
  onUpdate?: (
    bookingId: string,
    data: {
      startTime?: string;
      endTime?: string;
      isFullDay?: boolean;
      status?: string;
      notes?: string;
    }
  ) => Promise<void>;
  onCancel?: (bookingId: string) => Promise<void>;
  products: BookableProduct[];
  initialData?: {
    startTime: string;
    endTime: string;
    isFullDay?: boolean;
  };
  editingBooking?: BookingApi | null;
}

interface CustomerOption {
  id: string;
  name: string;
  email: string;
}

export default function BookingModal({
  open,
  onClose,
  onSave,
  onUpdate,
  onCancel,
  products,
  initialData,
  editingBooking,
}: BookingModalProps) {
  const userContext = useUser();
  const token = userContext?.userData?.auth;
  const modal = useGlobalModal();

  const [productId, setProductId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [isFullDay, setIsFullDay] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  const customerFieldRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        customerFieldRef.current &&
        !customerFieldRef.current.contains(event.target as Node)
      ) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      if (editingBooking) {
        // Editing mode
        setProductId(editingBooking.productId);
        setCustomerId(editingBooking.customerId);
        setSelectedCustomer(
          editingBooking.customer
            ? {
                id: editingBooking.customer.id,
                name: editingBooking.customer.name || "",
                email: editingBooking.customer.email,
              }
            : null
        );
        setCustomerSearch(editingBooking.customer?.name || "");
        const start = new Date(editingBooking.startTime);
        const end = new Date(editingBooking.endTime);
        setStartDate(start.toISOString().split("T")[0]);
        setStartTime(start.toTimeString().slice(0, 5));
        setEndDate(end.toISOString().split("T")[0]);
        setEndTime(end.toTimeString().slice(0, 5));
        setIsFullDay(editingBooking.isFullDay);
        setNotes(editingBooking.notes || "");
      } else if (initialData) {
        // Creating mode with pre-selected time
        const start = new Date(initialData.startTime);
        const end = new Date(initialData.endTime);
        setStartDate(start.toISOString().split("T")[0]);
        setStartTime(start.toTimeString().slice(0, 5));
        setEndDate(end.toISOString().split("T")[0]);
        setEndTime(end.toTimeString().slice(0, 5));
        setIsFullDay(initialData.isFullDay || false);
        setProductId("");
        setCustomerId("");
        setSelectedCustomer(null);
        setCustomerSearch("");
        setNotes("");
      } else {
        // Reset form
        setProductId("");
        setCustomerId("");
        setSelectedCustomer(null);
        setCustomerSearch("");
        setStartDate("");
        setStartTime("09:00");
        setEndDate("");
        setEndTime("10:00");
        setIsFullDay(false);
        setNotes("");
      }
    }
  }, [open, initialData, editingBooking]);

  useEffect(() => {
    const searchCustomers = async () => {
      if (!token || customerSearch.length < 2) {
        setCustomers([]);
        return;
      }
      setLoadingCustomers(true);
      try {
        const resp = await api.get("/customer", {
          params: { query: customerSearch, pageSize: 10 },
          headers: { Authorization: `Bearer ${token}` },
        });
        const raw = (resp && (resp as any).data) ? (resp as any).data : resp;
        const items = Array.isArray(raw.items) ? raw.items : [];
        setCustomers(
          items.map((c: any) => ({ id: c.id, name: c.name || "", email: c.email }))
        );
      } catch {
        setCustomers([]);
      } finally {
        setLoadingCustomers(false);
      }
    };

    const timeout = setTimeout(searchCustomers, 300);
    return () => clearTimeout(timeout);
  }, [customerSearch, token]);

  const handleSelectCustomer = (customer: CustomerOption) => {
    setSelectedCustomer(customer);
    setCustomerId(customer.id);
    setCustomerSearch(customer.name || customer.email);
    setShowCustomerDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingBooking) {
      if (!productId) {
        modal?.show({
          type: "warning",
          title: "Campo obrigatório",
          message: "Selecione um espaço/comodidade.",
          autoClose: 3000,
        });
        return;
      }
      if (!customerId) {
        modal?.show({
          type: "warning",
          title: "Campo obrigatório",
          message: "Selecione um cliente.",
          autoClose: 3000,
        });
        return;
      }
    }

    if (!startDate || !endDate) {
      modal?.show({
        type: "warning",
        title: "Campo obrigatório",
        message: "Preencha as datas.",
        autoClose: 3000,
      });
      return;
    }

    const startDateTime = isFullDay
      ? `${startDate}T00:00:00`
      : `${startDate}T${startTime}:00`;
    const endDateTime = isFullDay
      ? `${endDate}T23:59:59`
      : `${endDate}T${endTime}:00`;

    if (new Date(startDateTime) >= new Date(endDateTime)) {
      modal?.show({
        type: "warning",
        title: "Datas inválidas",
        message: "A data/hora de início deve ser anterior à de término.",
        autoClose: 3000,
      });
      return;
    }

    setSaving(true);
    try {
      if (editingBooking && onUpdate) {
        await onUpdate(editingBooking.id, {
          startTime: startDateTime,
          endTime: endDateTime,
          isFullDay,
          notes: notes || undefined,
        });
      } else {
        await onSave({
          productId,
          customerId,
          startTime: startDateTime,
          endTime: endDateTime,
          isFullDay,
          notes: notes || undefined,
        });
      }
      onClose();
    } catch (err: any) {
      console.error("Erro ao salvar reserva:", err);
      const msg = err.response?.data?.message || "Erro ao salvar reserva.";
      modal?.show({ type: "error", title: "Erro", message: msg, autoClose: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!editingBooking || !onCancel) return;
    
    if (!window.confirm("Tem certeza que deseja cancelar esta reserva?")) return;

    setSaving(true);
    try {
      await onCancel(editingBooking.id);
      onClose();
    } catch (err: any) {
      console.error("Erro ao cancelar reserva:", err);
      const msg = err.response?.data?.message || "Erro ao cancelar reserva.";
      modal?.show({ type: "error", title: "Erro", message: msg, autoClose: 3000 });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const isEditing = !!editingBooking;
  const isCancelled = editingBooking?.status === "CANCELLED";

  return (
    <div className="booking-modal-overlay" onClick={onClose}>
      <div className="booking-modal" onClick={(e) => e.stopPropagation()}>
        <button className="booking-modal-close" onClick={onClose}>
          ✕
        </button>
        <h2 className="booking-modal-title">
          {isEditing ? "Detalhes da Reserva" : "Nova Reserva"}
        </h2>

        <form className="booking-form" onSubmit={handleSubmit}>
          {/* Product Selection (only for new bookings) */}
          {!isEditing && (
            <div className="booking-field">
              <label>Espaço/Comodidade</label>
              <select
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                className="booking-select"
              >
                <option value="">Selecione um espaço...</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Show product name when editing */}
          {isEditing && editingBooking?.product && (
            <div className="booking-field">
              <label>Espaço/Comodidade</label>
              <input
                type="text"
                value={editingBooking.product.name}
                disabled
                className="booking-input"
              />
            </div>
          )}

          {/* Customer Selection (only for new bookings) */}
          {!isEditing && (
            <div className="booking-field booking-customer-field" ref={customerFieldRef}>
              <label>Cliente</label>
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  setShowCustomerDropdown(true);
                  if (!e.target.value) {
                    setSelectedCustomer(null);
                    setCustomerId("");
                  }
                }}
                onFocus={() => setShowCustomerDropdown(true)}
                placeholder="Digite pelo menos 2 caracteres..."
                className="booking-input"
                autoComplete="off"
              />
              {showCustomerDropdown && customers.length > 0 && (
                <div className="booking-customer-dropdown">
                  {customers.map((c) => (
                    <div
                      key={c.id}
                      className="booking-customer-option"
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent input blur
                        handleSelectCustomer(c);
                      }}
                    >
                      <span className="customer-name">{c.name || "Sem nome"}</span>
                      <span className="customer-email">{c.email}</span>
                    </div>
                  ))}
                </div>
              )}
              {loadingCustomers && (
                <div className="booking-customer-loading">Buscando...</div>
              )}
              {showCustomerDropdown && customerSearch.length >= 2 && !loadingCustomers && customers.length === 0 && (
                <div className="booking-customer-loading">Nenhum cliente encontrado</div>
              )}
              {selectedCustomer && (
                <div className="booking-selected-customer">
                  ✓ Selecionado: {selectedCustomer.name || selectedCustomer.email}
                </div>
              )}
            </div>
          )}

          {/* Show customer name when editing */}
          {isEditing && editingBooking?.customer && (
            <div className="booking-field">
              <label>Cliente</label>
              <input
                type="text"
                value={editingBooking.customer.name || editingBooking.customer.email}
                disabled
                className="booking-input"
              />
            </div>
          )}

          {/* Full Day Toggle */}
          <div className="booking-field booking-checkbox-field">
            <label>
              <input
                type="checkbox"
                checked={isFullDay}
                onChange={(e) => setIsFullDay(e.target.checked)}
                disabled={isCancelled}
              />
              Dia inteiro
            </label>
          </div>

          {/* Date/Time Selection */}
          <div className="booking-datetime-row">
            <div className="booking-field">
              <label>Data início</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (!endDate) setEndDate(e.target.value);
                }}
                className="booking-input"
                disabled={isCancelled}
              />
            </div>
            {!isFullDay && (
              <div className="booking-field">
                <label>Hora início</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="booking-input"
                  disabled={isCancelled}
                />
              </div>
            )}
          </div>

          <div className="booking-datetime-row">
            <div className="booking-field">
              <label>Data término</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="booking-input"
                disabled={isCancelled}
              />
            </div>
            {!isFullDay && (
              <div className="booking-field">
                <label>Hora término</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="booking-input"
                  disabled={isCancelled}
                />
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="booking-field">
            <label>Observações</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anotações sobre a reserva..."
              className="booking-textarea"
              rows={3}
              disabled={isCancelled}
            />
          </div>

          {/* Status badge when editing */}
          {isEditing && (
            <div className="booking-status">
              <span className={`booking-status-badge status-${editingBooking?.status?.toLowerCase()}`}>
                {editingBooking?.status === "CONFIRMED" && "Confirmada"}
                {editingBooking?.status === "CANCELLED" && "Cancelada"}
                {editingBooking?.status === "COMPLETED" && "Concluída"}
              </span>
            </div>
          )}

          {/* Actions */}
          <div className="booking-actions">
            {isEditing && !isCancelled && onCancel && (
              <button
                type="button"
                className="booking-btn booking-btn-danger"
                onClick={handleCancelBooking}
                disabled={saving}
              >
                Cancelar Reserva
              </button>
            )}
            <div className="booking-actions-right">
              <button
                type="button"
                className="booking-btn booking-btn-ghost"
                onClick={onClose}
              >
                Fechar
              </button>
              {!isCancelled && (
                <button
                  type="submit"
                  className="booking-btn booking-btn-primary"
                  disabled={saving}
                >
                  {saving ? "Salvando..." : isEditing ? "Atualizar" : "Criar Reserva"}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

